'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import UIButton from 'UIComponents/button/UIButton';
import UITile from 'UIComponents/tile/UITile';
import { useHistory, useLocation } from 'react-router-dom';
import { FLAT_EARTH, OLAF, KOALA, OBSIDIAN, FLINT } from 'HubStyleTokens/colors';
import { parse, stringify } from 'hub-http/helpers/params';
import FullScreenModal from './FullScreenModal';
import UIFlex from 'UIComponents/layout/UIFlex';
import { FormattedMessage } from 'LearningCenterUI/components/I18n';
import { PRIORITY_WEB_FONT } from 'HubStyleTokens/misc';
const ColoredUIButton = styled(UIButton).withConfig({
  displayName: "MobileFilterPanel__ColoredUIButton",
  componentId: "sc-1rkvduv-0"
})(["color:", " !important;background-color:", " !important;font-size:14px;line-height:16px;font-family:", ";font-weight:500;padding:11px 24px !important;margin-left:12px;margin-top:0;border-radius:3px;"], props => props.color, props => props.backgroundcolor, PRIORITY_WEB_FONT);
const NumResultsUITile = styled(UITile).withConfig({
  displayName: "MobileFilterPanel__NumResultsUITile",
  componentId: "sc-1rkvduv-1"
})(["padding:12px 20px;border:none;border-radius:0;background-color:", ";color:", ";"], FLAT_EARTH, OLAF);
export const MobileFilterPanel = (_ref) => {
  let {
    open,
    onCloseClick,
    totalFilteredItemsCount,
    children,
    filterTypes = []
  } = _ref,
      restProps = _objectWithoutPropertiesLoose(_ref, ["open", "onCloseClick", "totalFilteredItemsCount", "children", "filterTypes"]);

  const location = useLocation();
  const history = useHistory();
  const originalSearch = useRef(location.search);
  useEffect(() => {
    if (open) {
      originalSearch.current = location.search;
    } // only want to update on open prop change
    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [open]);
  const handleCloseClick = useCallback(() => {
    history.replace(Object.assign({}, location, {
      search: originalSearch.current
    }));
    onCloseClick();
  }, [history, location, onCloseClick]);
  const clearAllowedFilters = useCallback(() => {
    const params = parse(location.search.slice(1));
    Object.keys(params).forEach(filterType => {
      if (filterTypes.includes(filterType)) {
        delete params[filterType];
      }
    });
    history.replace(`${location.pathname}?${stringify(params)}`);
  }, [filterTypes, history, location.pathname, location.search]);
  const applyDisabled = originalSearch.current === location.search;
  const clearDisabled = !location.search;
  return /*#__PURE__*/_jsx(FullScreenModal, Object.assign({
    headerContent: /*#__PURE__*/_jsxs(UIFlex, {
      justify: "end",
      children: [/*#__PURE__*/_jsx(ColoredUIButton, {
        color: clearDisabled ? FLINT : OLAF,
        "data-test-id": "clear-filters",
        disabled: clearDisabled,
        onClick: clearAllowedFilters,
        responsive: false,
        use: "unstyled",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.mobileFilters.clearAll"
        })
      }), /*#__PURE__*/_jsx(ColoredUIButton, {
        backgroundcolor: applyDisabled ? FLINT : KOALA,
        color: OBSIDIAN,
        "data-test-id": "apply-filters",
        disabled: applyDisabled,
        onClick: onCloseClick,
        responsive: false,
        use: "unstyled",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.mobileFilters.apply"
        })
      })]
    }),
    onCloseClick: handleCloseClick,
    open: open,
    subheader: /*#__PURE__*/_jsx(NumResultsUITile, {
      distance: "flush",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.mobileFilters.numResults",
        options: {
          numResults: totalFilteredItemsCount
        }
      })
    })
  }, restProps, {
    children: children
  }));
};
MobileFilterPanel.propTypes = {
  children: PropTypes.node,
  filterTypes: PropTypes.array,
  onCloseClick: PropTypes.func.isRequired,
  open: PropTypes.bool,
  totalFilteredItemsCount: PropTypes.number
};